// src/riskgpt/src/CauseTable.jsx
import React, { useState } from 'react';
import {
  Table, Paper, TextInput, Select, ActionIcon, MultiSelect,
  Stack, Text
} from '@mantine/core';
import { IconAdjustments } from '@tabler/icons-react'; // Instead of IconSettings

export default function CauseTable({
  causeTemplate = {},
  evalTemplate  = {},
  answers       = {},
  onEvalChange,
  onDeepValuesChange,
  onDeepDetailChange,
  toTitleCase
}) {
  const toTitleCaseSafe = (s) => {
    if (!s) return '';
    // Normalize: space around ampersands, split camelCase/PascalCase, turn separators into spaces
    const normalized = String(s)
      .replace(/&/g, ' & ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const tokens = normalized.split(' ').filter(Boolean).map((w) => {
      const lower = w.toLowerCase();
      if (lower === 'and' || lower === '&') return '&';
      // Preserve acronyms (2+ uppercase/numeric chars)
      const isAcronym = w.length >= 2 && /^[A-Z0-9]+$/.test(w);
      return isAcronym ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    });

    // Ensure clean spacing around ampersands
    return tokens.join(' ').replace(/\s*&\s*/g, ' & ');
  };
  const format = toTitleCase || toTitleCaseSafe;

  console.groupCollapsed('🔍 [CauseTable] render');
  console.log('causeTemplate:', causeTemplate);
  console.log('evalTemplate:', evalTemplate);
  console.log('answers:', answers);
  console.groupEnd();

  const causes = Object.keys(causeTemplate);
  // build list with "Other" (case/space insensitive) always last
  const critKeysUnordered = Object.keys(evalTemplate);
  const isOther = k => k.trim().toLowerCase() === 'other';

  const critKeys = [
    ...critKeysUnordered.filter(k => !isOther(k)),
    ...critKeysUnordered.filter(k => isOther(k))
  ];

  // Deep-dive open/close state for each cause
  const [deepOpen, setDeepOpen] = useState({});
  const handleToggle = causeKey =>
    setDeepOpen(prev => ({ ...prev, [causeKey]: !prev[causeKey] }));

  const renderCell = (causeKey, crit) => {
    const tpl       = evalTemplate[crit];
    const persisted = answers[causeKey]?.evaluation?.[crit]?.value ?? '';

    // if it's a select, render a dropdown
    if (tpl && tpl.type === 'select') {
      return (
        <Select
          value={persisted}
          onChange={value => {
            console.log('[CauseTable] onEvalChange (select)', {
              causeKey, crit, newValue: value
            });
            onEvalChange(causeKey, crit, value);
          }}
          data={(tpl.options || []).map(option => ({
            value: option,
            label: option
          }))}
          placeholder="Select option"
          clearable
          size="sm"
        />
      );
    }

    // fallback to text input
    return (
      <TextInput
        value={persisted}
        onChange={e => {
          console.log('[CauseTable] onEvalChange (text)', {
            causeKey, crit, newValue: e.target.value
          });
          onEvalChange(causeKey, crit, e.target.value);
        }}
        size="sm"
      />
    );
  };

  return (
    <Paper withBorder>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Cause</Table.Th>
            {critKeys.map(k => (
              <Table.Th key={k}>{format(k)}</Table.Th>
            ))}
            <Table.Th>Deep Dive</Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {causes.map(causeKey => (
            <React.Fragment key={causeKey}>
              <Table.Tr>
              <Table.Td style={{ textAlign: 'left' }}>{format(causeKey)}</Table.Td>
                {critKeys.map(crit => (
                  <Table.Td key={crit}>
                    {renderCell(causeKey, crit)}
                  </Table.Td>
                ))}
                <Table.Td>
                  <ActionIcon
                    size="sm"
                    onClick={() => handleToggle(causeKey)}
                    color={deepOpen[causeKey] ? "blue" : "gray"}
                    variant={deepOpen[causeKey] ? "filled" : "subtle"}
                  >
                    <IconAdjustments size={16} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>

              {deepOpen[causeKey] && (
                <Table.Tr>
                  <Table.Td colSpan={critKeys.length + 2}>
                    <Stack gap="md" p="md">
                      {/* Deep Dive: multiselect of options */}
                      <MultiSelect
                        label={`Deep Dive: ${format(causeKey)}`}
                        placeholder="Select options"
                        value={answers[causeKey]?.deepValues || []}
                        onChange={values => onDeepValuesChange(causeKey, values)}
                        data={(causeTemplate[causeKey]?.options || []).map(opt => ({
                          value: opt,
                          label: opt
                        }))}
                        searchable
                        clearable
                        size="sm"
                      />

                      {/* Details for each selected deep value */}
                      {(answers[causeKey]?.deepValues || []).length > 0 && (
                        <Table>
                          <Table.Tbody>
                            {(answers[causeKey]?.deepValues || []).map(option => (
                              <Table.Tr key={option}>
                                <Table.Td style={{ width: 200 }}>
                                  <Text size="sm" fw={500}>{option}</Text>
                                </Table.Td>
                                <Table.Td>
                                  <TextInput
                                    value={answers[causeKey]?.deepDetails?.[option] || ''}
                                    onChange={e => onDeepDetailChange(causeKey, option, e.target.value)}
                                    placeholder={`Details for ${option}`}
                                    size="sm"
                                  />
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      )}
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              )}
            </React.Fragment>
          ))}
        </Table.Tbody>
      </Table>
    </Paper>
  );
}